import * as React from 'react';

import { observer } from 'mobx-react';

import { exportFile } from 'common-utils';
import { AdvanceTable, IBasicTableColumn, DangerAlert, IconTextButton, showConfirmDialogWithoutCancel } from 'ui-lib';

import { DeviceLogsStore } from '../../../../../store/device-details/device-logs-store';
import { IDeviceTableProps } from './components';
import devicesCss from './devices.css';
import { ILogOptions } from 'dto/device-dto';
import { DATE_TIME_FORMAT, formatDate } from 'common-utils/date-utils';
import { useDeviceStore } from 'store';

export type IDeviceLogsProps = IDeviceTableProps & { trampolineVersion?: string; platform: string };

export const useDeviceLogsStore = (props: IDeviceLogsProps): DeviceLogsStore => {
    const devicesStore = useDeviceStore();
    return React.useMemo(() => {
        const store = new DeviceLogsStore({
            accountId: props.accountId,
            uid: props.uid,
            devicesStore: devicesStore,
            serialNumber: props.serialNumber,
            platform: props.platform,
        });
        store.initialize();
        return store;
    }, [props.platform]);
};

export const getDeviceLogsColumn = (extraProps?: Partial<IBasicTableColumn>): IBasicTableColumn => ({
    code: 'data',
    title: 'Device Logs',
    dataIndex: 'data',
    defaultSortOrder: 'descend',
    sorter: (a: ILogOptions, b: ILogOptions) => a.timestamp?.localeCompare(b.timestamp),
    ...extraProps,
});

export const exportDeviceLogs = (store: DeviceLogsStore, downloadLogName: string, deviceLogs: string[]): void => {
    if (deviceLogs.length === 0) {
        showConfirmDialogWithoutCancel({
            title: 'No logs found',
            content: 'No logs data found for the provided type',
            okText: 'OK',
        });
        return;
    }
    const fileContent = deviceLogs.join('\n');

    exportFile(
        `deviceLogs_${downloadLogName}_${formatDate(store.dateFrom.toDate(), DATE_TIME_FORMAT.DEFAULT)}_${formatDate(store.dateTo.toDate(), DATE_TIME_FORMAT.DEFAULT)}`,
        fileContent,
        'text/plain;',
    );
};

interface IDeviceLogsTableProps {
    store: DeviceLogsStore;
    columns: IBasicTableColumn[];
    datePicker: React.ReactElement;
    isProcessing: boolean;
    onDownload: () => void;
    children?: React.ReactNode;
}

export const DeviceLogsTable = observer((props: IDeviceLogsTableProps) => {
    const { store } = props;
    return (
        <div className={devicesCss.tabPane}>
            {store.error != undefined && (
                <DangerAlert closable={false} message='Error' description={store.error} />
            )}
            <AdvanceTable
                title=''
                loadingState={store.loading}
                table={{
                    columns: props.columns,
                    dataSource: store.displayDeviceLogs(store.defaultLogType),
                }}
                toolbar={{
                    extraItems: [
                        props.datePicker,
                        <IconTextButton
                            key='download'
                            label='Download'
                            type='primary'
                            loading={props.isProcessing}
                            onClick={props.onDownload}
                        />,
                    ],
                    onReload: () => store.fetchLatestData(),
                }}
            />
            {props.children}
        </div>
    );
});
